import React, { useEffect, useRef, useState } from "react";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { SiAccenture, SiCisco, SiOracle } from "react-icons/si";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import {
  SiteShell,
  SectionHead,
  NextChapter,
} from "../components/BrandPrimitives";
import { careerRoles } from "../data/careerRoles";
import CareerInterviewProcess from "../components/CareerInterviewProcess";
import {
  APPLICATION_LIMITS,
  formatApplicationEvidence,
  newApplicationId,
  sendApplication,
  validateApplication,
} from "../utils/careerApplication";
import { PAGE_META } from "../utils/shareMeta";
import {
  foundingExperience,
  emergingTeamExperience,
  talentFrontiers,
} from "../data/careerPositioning";
import "../styles/brand-editorial.css";
import "../styles/brand-careers.css";

function ExperienceWordmark({ name }) {
  if (name === "Oracle") {
    return (
      <span className="career-company-wordmark">
        <SiOracle aria-hidden="true" className="career-oracle" />
        Oracle
      </span>
    );
  }
  if (name === "Cisco") {
    return (
      <span className="career-company-wordmark">
        <SiCisco aria-hidden="true" className="career-cisco" />
        Cisco
      </span>
    );
  }
  return (
    <span className={name === "Accenture" ? "career-accenture" : ""}>
      {name === "Accenture" && <SiAccenture aria-hidden="true" />}
      {name}
    </span>
  );
}

const OPEN_APPLICATION = { id: "open_application", title: "Open application" };
const emptyApplication = () => ({ name: "", email: "", github: "", linkedin: "", portfolio_url: "", work_url: "", code_url: "", proof: "", depth: "", website: "" });

export default function BrandCareersPage() {
  const [openRole, setOpenRole] = useState(null);
  const [applyRole, setApplyRole] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyApplication);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [copyState, setCopyState] = useState("");
  const manualCopy = useRef(null);
  const drafts = useRef(new Map());
  const inFlight = useRef(false);
  const feedback = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (error?.field) document.getElementById("apply-" + error.field)?.focus();
    else if (error || receipt) feedback.current?.focus();
  }, [error, receipt]);
  useEffect(() => {
    if (copyState === "manual") {
      manualCopy.current?.focus();
      manualCopy.current?.select();
    }
  }, [copyState]);
  const applicationText = [
    "Application: " + (applyRole?.title || ""),
    ...(error?.reference ? ["Application reference: " + error.reference] : []),
    "Name: " + form.name.trim(),
    "Reply email: " + form.email.trim(),
    "LinkedIn: " + form.linkedin.trim(),
    "", "Evidence of exceptional ability in this field:", formatApplicationEvidence(form),
    "", "Another area of unusual depth:", form.depth.trim(),
  ].join("\n");
  const copyApplication = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(applicationText);
      if (mounted.current) setCopyState("copied");
    } catch {
      if (mounted.current) setCopyState("manual");
    }
  };
  const openApply = (role) => {
    if (inFlight.current) return;
    const draft = drafts.current.get(role.id) || { form: emptyApplication() };
    drafts.current.set(role.id, draft);
    setApplyRole(role);
    setCopyState("");
    setError(draft.error || null);
    setForm(draft.form);
    setReceipt(draft.receipt || null);
  };
  const closeApply = () => {
    if (!inFlight.current) setApplyRole(null);
  };
  const updateField = (field, value) => {
    if (inFlight.current) return;
    const next = { ...form, [field]: value };
    drafts.current.set(applyRole.id, { form: next });
    setForm(next);
    setCopyState("");
    setError(null);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current || receipt || !applyRole || error?.retryable === false) return;
    const validation = validateApplication(form);
    if (validation.error) {
      setError(validation.error);
      return;
    }
    const draft = drafts.current.get(applyRole.id);
    let requestId;
    try {
      requestId = draft.requestId || newApplicationId();
    } catch (failure) {
      setError({ message: failure.message });
      return;
    }
    drafts.current.set(applyRole.id, { ...draft, requestId });
    inFlight.current = true;
    setSubmitting(true);
    setCopyState("");
    setError(null);
    try {
      const result = await sendApplication({
        request_id: requestId,
        role_id: applyRole.id,
        ...validation.fields,
        website: form.website,
      });
      // Keep the receipt, but release the candidate's answers after confirmed receipt.
      drafts.current.set(applyRole.id, { form: emptyApplication(), receipt: result.application_id });
      if (mounted.current) {
        setReceipt(result.application_id);
        setForm(emptyApplication());
      }
    } catch (failure) {
      const applicationError = {
        message: failure.message || "We could not submit your application. Please try again.",
        field: failure.field,
        reference: failure.reference || draft.error?.reference,
        retryable: failure.retryable,
      };
      drafts.current.set(applyRole.id, { ...drafts.current.get(applyRole.id), error: applicationError });
      if (mounted.current) setError(applicationError);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };
  return (
    <SiteShell
      title={PAGE_META.careers.title}
      description={PAGE_META.careers.description}
      className="brand-careers"
      image={PAGE_META.careers.image}
    >
      <section className="brand-editorial-hero brand-container">
        <span className="brand-eyebrow">Careers / The founding team</span>
        <h1>
          GrupaAI.
          <br />
          <span>
            The Pioneer <span className="career-lab-name">AI Lab.</span>
          </span>
        </h1>
        <div className="brand-editorial-intro">
          <p>
            We are assembling a founding team to lead the next era of AI:
            digital humanoid workers that deliver real business outcomes.
          </p>
          <div className="career-hero-actions">
            <a href="#roles" className="brand-button">
              Explore open roles <ArrowDownward fontSize="small" />
            </a>
            <a href="#interview-process" className="brand-inline-button">How we interview</a>
          </div>
        </div>
      </section>
      <section
        className="career-origins brand-container"
        aria-labelledby="career-origins-title"
      >
        <div className="career-section-heading">
          <span className="brand-eyebrow">01 / Founding experience</span>
          <h2 id="career-origins-title">
            Built the software. Lived the operations.
          </h2>
          <p>
            Tech companies are software and operations. Software scales. Too
            much of the work behind it is still manual. We are building digital
            humanoid agents to automate operations at scale.
          </p>
        </div>
        <div className="career-origin-grid">
          {foundingExperience.map((origin) => (
            <div className="career-origin" key={origin.discipline}>
              <h3>{origin.discipline}</h3>
              {origin.headline ? (
                <strong className="career-origin-headline">
                  {origin.headline}
                </strong>
              ) : (
                <ul aria-label={origin.discipline + " experience"}>
                  {origin.organizations.map((name) => (
                    <li key={name}>
                      <ExperienceWordmark name={name} />
                    </li>
                  ))}
                </ul>
              )}
              <p>{origin.description}</p>
            </div>
          ))}
        </div>
        <div className="career-advisory">
          <span>Advisory experience</span>
          <strong>
            Teleperformance <span aria-hidden="true">/</span> TP.ai
          </strong>
          <p>
            Prior experience, not organizational affiliation or endorsement.
          </p>
        </div>
      </section>
      <section
        className="career-emerging brand-container"
        aria-labelledby="career-emerging-title"
      >
        <h2 id="career-emerging-title">The team taking shape.</h2>
        <div className="career-emerging-table">
          {emergingTeamExperience.map((group, index) => (
            <div className="career-emerging-row" key={group.status}>
              <h3 id={"career-team-status-" + index}>{group.status}</h3>
              <ul
                className="career-emerging-organizations"
                aria-labelledby={"career-team-status-" + index}
              >
                {group.organizations.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="career-affiliation-note">
          Individual backgrounds, not organizational affiliations or endorsements.
        </p>
      </section>
      <section className="brand-career-intro brand-container">
        <div>
          <span className="brand-eyebrow">02 / The ambition</span>
          <h2>
            Lead the frontier.
            <br />
            Deliver the work.
          </h2>
        </div>
        <div>
          <p>
            Our ambition is to set the standard that other AI labs follow. The
            measure is useful work: digital humanoids that see, reason, act, and
            carry a mission through to a verified outcome.
          </p>
          <p>
            We call the distance between frontier intelligence and dependable,
            affordable execution the Pioneer Lab Gap. Closing it takes invention
            across the stack: kernels, models, perception, agents, and product.
            One worker. Coordinated teams. Eventually, autonomous organizations.
          </p>
          <p>
            We are building toward <strong>trillions of autonomous digital workers</strong>,
            provisioned on demand for organizations. Reliable, secure, 24/7 work
            at that scale demands exceptional people in AI, agents, engineering,
            research, and security. That is the team we are assembling.
          </p>
        </div>
      </section>
      <section className="brand-career-target brand-container">
        <strong>
          &lt; 0.3&cent; <small>per worker-hour</small>
        </strong>
        <p>
          Our engineering cost target. Making capable workers widely accessible
          means pursuing reliability and radical efficiency together. This is a
          research ambition, not a current price or achieved benchmark.
        </p>
      </section>
      <section className="brand-career-target brand-career-power-target brand-container">
        <strong>
          20 W <small>whole-device inference</small>
        </strong>
        <p>
          <strong>Invent the next architecture.</strong> We are pursuing original
          language-model architectures that outperform strong transformer baselines
          in useful worker capability, reliability, and efficiency. Our research
          target is sustained inference within 20 W for the entire device,
          including compute, memory, and device overhead, at a defined quality,
          latency, and throughput. This is an ambition to prove, not an achieved
          result.
        </p>
      </section>
      <section
        className="career-talent brand-container"
        aria-labelledby="career-talent-title"
      >
        <div className="career-section-heading">
          <span className="brand-eyebrow">03 / Who we are looking for</span>
          <h2 id="career-talent-title">
            Exceptional depth.
            <br />
            Original thinkers.
          </h2>
          <p>
            We are looking for people who have pushed these fields forward, from
            leading AI labs and research groups to teams that ship at global
            scale. Come own a hard part of the worker, from first principles to
            production.
          </p>
        </div>
        <div className="career-talent-table">
          <div className="career-talent-labels" aria-hidden="true">
            <span>The discipline</span>
            <span>Backgrounds we are seeking</span>
            <span>The evidence we value</span>
          </div>
          {talentFrontiers.map((frontier, i) => (
            <article className="career-talent-row" key={frontier.title}>
              <h3>
                <span>{String(i + 1).padStart(2, "0")}</span>
                {frontier.title}
              </h3>
              <p className="career-talent-organizations">
                {frontier.organizations}
              </p>
              <p className="career-talent-evidence">{frontier.evidence}</p>
            </article>
          ))}
        </div>
        <p className="career-affiliation-note">
          These are recruiting interests, not current team affiliations or
          partnerships.
        </p>
        <div className="career-proof">
          <h3>Your work is the credential.</h3>
          <p>
            Exceptional work earns the conversation. Show us the model you trained, the system you made
            reliable, the benchmark you advanced, or the product people depend
            on. If your path looks different, the same bar applies. We want to
            see your work. Include a direct link to a project, demo, paper, or
            benchmark we can review. Source code is optional.
          </p>
          <button
            className="brand-inline-button"
            onClick={() => openApply(OPEN_APPLICATION)}
          >
            Introduce your work
          </button>
        </div>
      </section>
      <CareerInterviewProcess />
      <section id="roles" className="brand-container brand-section">
        <SectionHead
          eyebrow="05 / Open roles / Bay Area"
          title="A founding seat. A defining problem."
        >
          Eight technical disciplines. One connected research and engineering
          agenda: expert digital humanoid workers that people can depend on.
        </SectionHead>
        <div className="brand-role-list">
          {careerRoles.map((role, i) => (
            <article className="brand-role" key={role.title}>
              <button
                aria-expanded={openRole === i}
                aria-controls={"role-" + i}
                onClick={() => setOpenRole(openRole === i ? null : i)}
              >
                <span className="brand-role-title">
                  {role.title}
                  <small>{role.tag}</small>
                </span>
                <span>{role.location}</span>
                <Add />
              </button>
              {openRole === i && (
                <div id={"role-" + i} className="brand-role-detail">
                  <p>{role.mandate}</p>
                  <h3>What you will work on</h3>
                  <ul>
                    {role.stack.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <h3>Show us your work</h3>
                  <p>{role.evidence}</p>
                  <button
                    className="brand-button"
                    onClick={() => openApply(role)}
                  >
                    Apply for this role
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
        <p className="brand-careers-note">
          A different kind of contribution?{" "}
          <button
            className="brand-inline-button"
            onClick={() => openApply(OPEN_APPLICATION)}
          >
            Tell us what you could build.
          </button>
        </p>
      </section>
      <NextChapter
        title="Build with conviction."
        text="Get to know the ideas behind GrupaAI."
        to="/manifesto"
        label="Read the manifesto"
      />
      <Dialog
        open={Boolean(applyRole)}
        onClose={closeApply}
        fullWidth
        TransitionProps={{ onEntered: () => error?.field ? document.getElementById("apply-" + error.field)?.focus() : receipt || error ? feedback.current?.focus() : document.getElementById("apply-name")?.focus() }}
        maxWidth="sm"
        PaperProps={{ className: "career-application-dialog" }}
        aria-labelledby="application-title"
        aria-describedby={!receipt ? "application-description" : undefined}
      >
        <DialogTitle
          id="application-title"
          className="career-application-title"
        >
          {applyRole?.title}
          <IconButton
            aria-label="Close application"
            className="career-application-close"
            onClick={closeApply}
            disabled={submitting}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className="career-application-content">
          {receipt ? (
            <div className="career-application-receipt brand-form" role="status" tabIndex={-1} ref={feedback}>
              <span className="career-receipt-mark" aria-hidden="true">✓</span>
              <h3>Application received.</h3>
              <p>Thank you for sharing your work. Our team will review your application and contact you by email if there is a fit.</p>
              <p className="brand-form-note">Your reference: <span className="career-application-reference">{receipt}</span></p>
              <button type="button" className="brand-button" onClick={closeApply}>Done</button>
            </div>
          ) : (
            <form className="brand-form career-application-form" onSubmit={submit} noValidate aria-busy={submitting}>
              <p id="application-description" className="brand-form-note">
                Show us what you built. Submit your application here with a direct link to work or a demo we can review. GitHub, LinkedIn, and a work or demo link are required. Code and portfolio links are optional.
              </p>
              {[
                ["name", "Your name", "input"],
                ["email", "Email address", "input"],
                ["github", "GitHub profile URL", "input"],
                ["linkedin", "LinkedIn profile URL", "input"],
                ["work_url", "Link to your work or demo", "input", "Show us one thing you built, improved, or discovered that demonstrates exceptional ability in this field: a live product, recorded demo or technical walkthrough, research paper, or benchmark. Make sure we can access it. A description alone is not enough."],
                ["proof", "What did you personally accomplish in this field, and what proves it worked?", "textarea", "Describe your contribution, the difficult problem you solved, and the measurable result. Include scale, adoption, performance, or other evidence we can verify. Share only work you are authorized to show."],
                ["depth", "Where else have you developed unusual depth?", "textarea", "Tell us about another skill, craft, or pursuit you have taken seriously, and what you achieved or learned."],
                ["code_url", "Code or repository URL (optional)", "input", "Link to the relevant repository, pull request, or notebook if you can share it. Private source code is not required."],
                ["portfolio_url", "Portfolio URL (optional)", "input"],
              ].map(([key, label, type, hint]) => {
                const attributes = {
                  id: "apply-" + key,
                  name: key,
                  required: !["portfolio_url", "code_url"].includes(key),
                  disabled: submitting,
                  maxLength: APPLICATION_LIMITS[key],
                  value: form[key],
                  onChange: (e) => updateField(key, e.target.value),
                  "aria-invalid": error?.field === key || undefined,
                  "aria-describedby": [hint ? "apply-" + key + "-hint" : null, error?.field === key ? "application-error" : null].filter(Boolean).join(" ") || undefined,
                };
                return (
                  <div key={key} className={["name", "email", "github", "linkedin"].includes(key) ? "career-field-half" : undefined}>
                    <label htmlFor={"apply-" + key}>{label}</label>
                    {type === "textarea" ? <textarea {...attributes} /> : (
                      <input {...attributes}
                        type={key === "email" ? "email" : "text"}
                        inputMode={["github", "linkedin", "portfolio_url", "work_url", "code_url"].includes(key) ? "url" : undefined}
                        autoComplete={key === "name" ? "name" : key === "email" ? "email" : "url"}
                        autoCapitalize={key === "name" ? "words" : "none"}
                      />
                    )}
                    {hint && <p id={"apply-" + key + "-hint"} className="career-field-hint">{hint}</p>}
                  </div>
                );
              })}
              <div className="career-application-honeypot" aria-hidden="true">
                <label htmlFor="apply-website">Leave this field empty</label>
                <input id="apply-website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => updateField("website", e.target.value)} />
              </div>
              {error && <p id="application-error" className="brand-form-error" role="alert" tabIndex={-1} ref={feedback}>{error.message}</p>}
              {error && !error.field && (
                <div className="career-application-fallback">
                  {error.reference && <p className="brand-form-note">Application reference: <span className="career-application-reference">{error.reference}</span></p>}
                  <p className="brand-form-note">You can also copy your application and send it to <a href="mailto:team@grupa.ai">team@grupa.ai</a>.</p>
                  <button type="button" className="brand-inline-button" onClick={copyApplication}>Copy application</button>
                  {copyState === "copied" && <p className="brand-form-note" role="status">Copied. Paste it into an email to team@grupa.ai and send.</p>}
                  {copyState === "manual" && <>
                    <p className="brand-form-note" role="status">Automatic copying is unavailable. Select and copy the text below, then email it to team@grupa.ai.</p>
                    <textarea ref={manualCopy} aria-label="Application text to copy" readOnly value={applicationText} />
                  </>}
                </div>
              )}
              <button className="brand-button career-submit-button" type="submit" disabled={submitting || error?.retryable === false}>
                {submitting ? <><span className="career-submit-spinner" aria-hidden="true" />Submitting application…</> : error?.retryable === false ? "Contact us to resolve this application" : "Submit application"}
              </button>
              <p className="career-submission-status brand-form-note" role="status" aria-live="polite">
                {submitting ? "Please keep this window open while we confirm receipt." : "Your answers stay in this page if you close and reopen the form. They are cleared when you leave or refresh."}
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </SiteShell>
  );
}
